import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { PrintJobStatus, TicketedDestination } from '@sebastian/shared';
import { api } from '../lib/api';

/**
 * Query keys and shared hooks.
 *
 * Live updates are polling-based (see the Worker's /api/sync route for why).
 * Intervals are tuned per screen: the kitchen board is the most time-critical,
 * the admin screens barely need to refresh at all.
 */
export const POLL = {
  station: 4_000,
  floor: 6_000,
  order: 5_000,
  admin: 60_000,
  /** The till's queue: a waiter is standing at the table waiting for paper. */
  printStation: 2_500,
  /** Watching one job through to Printed, on the device that asked for it. */
  printJob: 1_500,
} as const;

export const keys = {
  settings: ['settings'] as const,
  tables: (includeInactive: boolean) => ['tables', includeInactive] as const,
  categories: (includeInactive: boolean) => ['categories', includeInactive] as const,
  categoryTree: (includeInactive: boolean) => ['categories', 'tree', includeInactive] as const,
  menuItems: (includeInactive: boolean) => ['menu-items', includeInactive] as const,
  modifiers: (includeInactive: boolean) => ['modifier-groups', includeInactive] as const,
  order: (id: string) => ['order', id] as const,
  activeOrder: (tableId: string) => ['order', 'active', tableId] as const,
  tickets: (destination: TicketedDestination) => ['tickets', destination] as const,
  history: (params: Record<string, unknown>) => ['orders', 'history', params] as const,
  printJobs: (params: Record<string, unknown>) => ['print-jobs', params] as const,
  printJob: (id: string) => ['print-jobs', 'one', id] as const,
  users: ['users'] as const,
  audit: (params: Record<string, unknown>) => ['audit', params] as const,
};

type Options<T> = Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, 'queryKey' | 'queryFn'>;

export function useSettings(options: Options<Awaited<ReturnType<typeof api.settings.get>>> = {}) {
  return useQuery({
    queryKey: keys.settings,
    queryFn: api.settings.get,
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useTables(includeInactive = false, live = true) {
  return useQuery({
    queryKey: keys.tables(includeInactive),
    queryFn: () => api.tables.list(includeInactive),
    refetchInterval: live ? POLL.floor : false,
  });
}

export function useCategoryTree(includeInactive = false) {
  return useQuery({
    queryKey: keys.categoryTree(includeInactive),
    queryFn: () => api.categories.tree(includeInactive),
    staleTime: 60_000,
  });
}

export function useCategories(includeInactive = false) {
  return useQuery({
    queryKey: keys.categories(includeInactive),
    queryFn: () => api.categories.list(includeInactive),
    staleTime: 60_000,
  });
}

/** `live` polls at the order-screen rate, so stock levels reflect what other staff are doing. */
export function useMenuItems(includeInactive = false, live = false) {
  return useQuery({
    queryKey: keys.menuItems(includeInactive),
    queryFn: () => api.menuItems.list({ includeInactive }),
    staleTime: live ? 0 : 60_000,
    refetchInterval: live ? POLL.order : false,
  });
}

export function useModifierGroups(includeInactive = false) {
  return useQuery({
    queryKey: keys.modifiers(includeInactive),
    queryFn: () => api.modifiers.list(includeInactive),
    staleTime: 60_000,
  });
}

export function useOrder(orderId: string | undefined, live = true) {
  return useQuery({
    queryKey: keys.order(orderId ?? 'none'),
    queryFn: () => api.orders.get(orderId!),
    enabled: Boolean(orderId),
    refetchInterval: live ? POLL.order : false,
  });
}

export function useActiveOrder(tableId: string | undefined) {
  return useQuery({
    queryKey: keys.activeOrder(tableId ?? 'none'),
    queryFn: () => api.orders.activeForTable(tableId!),
    enabled: Boolean(tableId),
  });
}

export function useTickets(destination: TicketedDestination, completedWithinMinutes = 0) {
  return useQuery({
    queryKey: [...keys.tickets(destination), completedWithinMinutes] as const,
    queryFn: () => api.station(destination).tickets(completedWithinMinutes),
    refetchInterval: POLL.station,
  });
}

/** The print queue, for the station screen and the queue badge. */
export function usePrintJobs(params: { status?: PrintJobStatus; limit?: number } = {}, live = true) {
  return useQuery({
    queryKey: keys.printJobs(params),
    queryFn: () => api.printJobs.list(params),
    refetchInterval: live ? POLL.printStation : false,
  });
}

export function useUsers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.users,
    queryFn: api.users.list,
    staleTime: 60_000,
    ...options,
  });
}

/** Invalidate everything a floor action can touch. */
export function useRefreshFloor() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['tables'] });
    void client.invalidateQueries({ queryKey: ['order'] });
    void client.invalidateQueries({ queryKey: ['tickets'] });
  };
}
